/**
 * Researcher Knowledge Admin Portal — top-level container.
 *
 * Section: docs/BRIEFING_RESEARCHER_KNOWLEDGE_PORTAL.md
 * Routes:  /admin/researcher-knowledge (handled in AdminApp.tsx)
 *
 * Tab structure (Phase 1 + Phase 4 — first ship, 2026-04-28):
 *   1. Overview          — corpus stats + recent curation activity
 *   2. Library           — browse research_sources (academic + practitioner)
 *   3. Pending Retags    — manual triage of low-conf retag proposals (split A/P)
 *   4. Frameworks        — browse 144 coaching methodologies + credibility
 *   5. Candidates        — Phase 4 Type B staging (empty until Phase 5+6)
 *   6. Polling Simulator — debug what the polling layer would return
 *
 * Future tabs (later phases):
 *   - Research Analytics  (Phase 2)
 *   - Activity Digest     (Phase 7)
 *   - Distribution List   (Phase 8)
 */
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Activity, BookOpen, Database, FlaskConical, Layers, Search, ShieldCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { OverviewTab } from './researcher-knowledge/OverviewTab';
import { LibraryTab } from './researcher-knowledge/LibraryTab';
import { PendingRetagsTab } from './researcher-knowledge/PendingRetagsTab';
import { CandidatesTab } from './researcher-knowledge/CandidatesTab';
import { FrameworksTab } from './researcher-knowledge/FrameworksTab';
import { PollingSimulatorTab } from './researcher-knowledge/PollingSimulatorTab';
import { DailyOpsTab } from './researcher-knowledge/DailyOpsTab';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function PortalContent() {
  return (
    <div className="mx-auto max-w-7xl space-y-4 px-4 py-6">
      <header className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Researcher Knowledge</h1>
        <p className="mt-1 text-sm text-gray-600">
          Browse, curate, and grow Coach Cedar's research library across
          academic, practitioner, and framework knowledge.
        </p>
      </header>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex h-auto flex-wrap gap-1 bg-gray-100 p-1">
          <TabsTrigger value="overview" className="gap-1">
            <ShieldCheck className="h-4 w-4" />
            <span>Overview</span>
          </TabsTrigger>
          <TabsTrigger value="library" className="gap-1">
            <BookOpen className="h-4 w-4" />
            <span>Library</span>
          </TabsTrigger>
          <TabsTrigger value="pending-retags" className="gap-1">
            <Layers className="h-4 w-4" />
            <span>Pending Retags</span>
          </TabsTrigger>
          <TabsTrigger value="frameworks" className="gap-1">
            <FlaskConical className="h-4 w-4" />
            <span>Frameworks</span>
          </TabsTrigger>
          <TabsTrigger value="candidates" className="gap-1">
            <Database className="h-4 w-4" />
            <span>Candidates</span>
          </TabsTrigger>
          <TabsTrigger value="polling-simulator" className="gap-1">
            <Search className="h-4 w-4" />
            <span>Polling Simulator</span>
          </TabsTrigger>
          <TabsTrigger value="daily-ops" className="gap-1">
            <Activity className="h-4 w-4" />
            <span>Daily Ops</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="library"><LibraryTab /></TabsContent>
        <TabsContent value="pending-retags"><PendingRetagsTab /></TabsContent>
        <TabsContent value="frameworks"><FrameworksTab /></TabsContent>
        <TabsContent value="candidates"><CandidatesTab /></TabsContent>
        <TabsContent value="polling-simulator"><PollingSimulatorTab /></TabsContent>
        <TabsContent value="daily-ops"><DailyOpsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

export function ResearcherKnowledgePortal() {
  return (
    <QueryClientProvider client={queryClient}>
      <PortalContent />
    </QueryClientProvider>
  );
}

export default ResearcherKnowledgePortal;
